import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'react-bootstrap';
import { Modal } from '../patterns';
import type { SprayCalibration } from '../../services/sprayService';
import { formatDateTime, formatDate } from '../../dateFormat';

interface Props {
  calibration: SprayCalibration;
  onClose: () => void;
}

/**
 * Read-only audit-history viewer for a calibration. Each editHistory entry
 * stores a `previousSnapshot` of the calibration before the edit; this modal
 * lists them newest-first with the editor + timestamp + a summary of the
 * pre-edit values.
 *
 * Track: machine_calibration_overrides_20260427 / Phase 3 / Task 5.
 */
const CalibrationHistoryModal: React.FC<Props> = ({ calibration, onClose }) => {
  const { t } = useTranslation();
  const history = calibration.editHistory ?? [];
  // Newest first.
  const sorted = [...history].sort(
    (a, b) => new Date(b.editedAt).getTime() - new Date(a.editedAt).getTime()
  );

  return (
    <Modal
      show
      onHide={onClose}
      size="lg"
      title={t('spray.machines.calibrations.historyTitle')}
    >
      <Modal.Body>
        {sorted.length === 0 ? (
          <p className="text-muted mb-0">
            {t('spray.machines.calibrations.historyEmpty')}
          </p>
        ) : (
          <div className="d-flex flex-column gap-3">
            {sorted.map((entry, idx) => {
              const snap = entry.previousSnapshot;
              return (
                <div key={idx} data-testid="history-entry" className="border rounded p-2">
                  <div className="small text-muted mb-2">
                    <strong>{t('spray.machines.calibrations.historyEditedBy')}:</strong>{' '}
                    {entry.editedBy}{' '}
                    <span className="ms-3">
                      <strong>{t('spray.machines.calibrations.historyEditedAt')}:</strong>{' '}
                      {formatDateTime(entry.editedAt)}
                    </span>
                  </div>
                  <div className="small">
                    <strong>{t('spray.machines.calibrations.historyPreviousValues')}:</strong>
                  </div>
                  <Table size="sm" className="mb-0 mt-1">
                    <tbody>
                      <tr>
                        <td>{t('spray.machines.calibrations.date')}</td>
                        <td>{snap?.date ? formatDate(snap.date, { timeZone: 'UTC' }) : '—'}</td>
                      </tr>
                      <tr>
                        <td>{t('spray.machines.calibrations.performedBy')}</td>
                        <td>{snap?.performedBy ?? '—'}</td>
                      </tr>
                      <tr>
                        <td>{t('spray.machines.calibrations.treeSpacing')}</td>
                        <td>{snap?.treeSpacing ?? '—'}</td>
                      </tr>
                      <tr>
                        <td>{t('spray.machines.calibrations.treesPerHectare')}</td>
                        <td>{snap?.treesPerHectare ?? '—'}</td>
                      </tr>
                      <tr>
                        <td>{t('spray.machines.calibrations.intensityLevels')}</td>
                        <td>
                          {(snap?.intensityLevels ?? []).map((lvl, i) => (
                            <span key={i} className="badge bg-secondary me-1">
                              {lvl.label}
                              {lvl.volumePerTree !== undefined ? ` · ${lvl.volumePerTree} L/tree` : ''}
                              {lvl.litresPerHectare !== undefined ? ` · ${lvl.litresPerHectare} L/ha` : ''}
                            </span>
                          ))}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CalibrationHistoryModal;
